import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '@/lib/contexts/AuthContext'
import { executeRecaptcha } from '@/lib/utils/recaptcha'
import {
  SystemCardShell,
  SystemCardHeading,
  SystemCardProgress,
} from '@/components/shared/layout'

const API_URL = import.meta.env.VITE_PUBLIC_API_URL || 'http://localhost:3000'
const RECAPTCHA_SITE_KEY = import.meta.env.VITE_PUBLIC_RECAPTCHA_SITE_KEY || ''

type Step = 'verifying' | 'ready' | 'error'

/**
 * Agent-led sign-up (backend `GET /agent.md`). The agent opens a session, the
 * person lands here, we confirm they are human and create the account on Free
 * start: the first 25,000 data points free, no card, API and MCP on. The agent's
 * poll completes as soon as the account exists.
 */
export default function AgentVerify() {
  const [searchParams] = useSearchParams()
  const { setToken } = useAuth()
  const token = searchParams.get('token') || ''

  const [step, setStep] = useState<Step>('verifying')
  const [errorMsg, setErrorMsg] = useState('')

  // Run reCAPTCHA invisibly on mount and create the account
  useEffect(() => {
    if (!token) {
      setErrorMsg('This link is missing its session. Ask your agent to start over.')
      setStep('error')
      return
    }

    const run = async () => {
      let captchaToken = ''
      try {
        captchaToken = await executeRecaptcha(RECAPTCHA_SITE_KEY, 'agent_signup')
      } catch {
        // Non-blocking — backend allows empty tokens in dev
      }
      await handleVerify(captchaToken)
    }

    run()
  }, [token])

  const handleVerify = async (captchaToken: string) => {
    try {
      const res = await fetch(`${API_URL}/api/v1/auth/agent-captcha-complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, captchaToken }),
        credentials: 'include',
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        if (data.error === 'session_expired') {
          setErrorMsg('This link has expired. Ask your agent to start over.')
        } else if (data.error === 'user_exists') {
          setErrorMsg('This email already has an account. Ask your agent to connect to it.')
        } else {
          setErrorMsg('Something went wrong on our side. Close this tab and ask your agent to try again.')
        }
        setStep('error')
        return
      }

      const data = await res.json()
      setToken(data.authToken)
      setStep('ready')
    } catch {
      setErrorMsg('Something went wrong on our side. Close this tab and ask your agent to try again.')
      setStep('error')
    }
  }

  return (
    <SystemCardShell>
      {step === 'verifying' && (
        <>
          <SystemCardHeading eyebrow="Almost there" title="Setting up your account">
            Linking this browser to the account your agent started for you.
          </SystemCardHeading>
          <SystemCardProgress label="Verifying" />
        </>
      )}

      {step === 'ready' && (
        <SystemCardHeading success eyebrow="You're in" title="Head back to your agent" />
      )}

      {step === 'error' && (
        <SystemCardHeading eyebrow="Almost there" title="This link didn't work">
          {errorMsg}
        </SystemCardHeading>
      )}
    </SystemCardShell>
  )
}
